import InvoicePdfCanvas from './InvoicePdfCanvas';
import { isAndroidInvoice, hasNativeInvoicePreview, previewNativeInvoice } from '../invoicePdfNative';
import { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { usePagedArray, Pager } from 'rentalui-finance';
import {
  createInvoice, updateInvoice, downloadInvoicePdf, fetchInvoicePdfBlob, getInvoiceTemplate, imageUrl,
  listInvoices, saveInvoiceTemplate, saveInvoicePdfBlob, setInvoiceStatus, uploadInvoiceLogo,
  recordInvoicePayment, getInvoiceSummary
} from '../api';

// manual_vs_automatic_invoicing.md - how the owner says their client paid.
const PAYMENT_METHODS = ['BANK_TRANSFER', 'UPI', 'CASH', 'CARD', 'NET_BANKING', 'WALLET', 'OTHER'];

// Payment / verification state -> a badge on the History row.
function paymentBadge(inv) {
  if (inv.revenueRecognisedAt) return <Badge bg="success">Verified</Badge>;
  if (inv.financeReview?.decision === 'DISPUTED') return <Badge bg="danger">Disputed</Badge>;
  if (inv.paymentStatus === 'PAID' || inv.paymentStatus === 'PARTIALLY_PAID') {
    return <Badge bg="warning" text="dark">Paid · awaiting verification</Badge>;
  }
  return <Badge bg="light" text="dark" className="border">Unpaid</Badge>;
}

// 08_owner_invoice_pdf_export.md - a real_estate rental owner's own manual invoice generator.
// Deliberately NOT the full booking-linked invoicing workflow the requirement doc describes: no
// "select a booking" step, no DRAFT/ISSUED/PAID lifecycle - the owner fills in an invoice by hand
// and exports it to PDF, and can save their own business/bank/terms/tax fields once as a reusable
// "format" so they don't retype them on every future invoice. Three tabs: New Invoice (uses the
// saved format), Invoice Format (edit + save it), History (past invoices, re-downloadable,
// disable-not-delete).

const rupees = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const todayISO = () => new Date().toISOString().slice(0, 10);

const emptySeller = { logoUrl: '', businessName: '', gstin: '', pan: '', addressLine1: '', city: '', state: '', pincode: '', mobile: '' };
const emptyBank = { bankName: '', accountHolder: '', accountNumber: '', ifsc: '', branch: '' };
const emptyCustomer = { name: '', gstin: '', addressLine1: '', city: '', state: '', pincode: '' };
const emptyAddress = { addressLine1: '', city: '', state: '', pincode: '' };
const newLineItem = (template) => ({ description: '', hsnSac: template?.defaultHsnSac || '', rate: '', qty: 1 });

// Shared by the "Preview & Download" button on a just-generated invoice and every History row -
// fetches the PDF once as a blob, renders it on canvas (native on Android), and saves it to
// disk from the Download button here (so opening a preview never means a wasted second fetch).
// Exported for preview and native-bridge regression tests.
export function PdfPreviewModal({ invoiceId, invoiceNumber, show, onHide }) {
  const android = isAndroidInvoice();
  const nativePreview = hasNativeInvoicePreview();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [blob, setBlob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show || !invoiceId) return undefined;
    let active = true;
    setLoading(true); setError(''); setMessage(''); setBlob(null);
    fetchInvoicePdfBlob(invoiceId)
      .then((b) => {
        if (!active) return;
        setBlob(b);
      })
      .catch(() => { if (active) setError('Unable to load the PDF preview.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [show, invoiceId, android]);

  const operate = async (preview) => {
    if (busy || !blob) return;
    setBusy(true); setError(''); setMessage('');
    try {
      const result = preview ? await previewNativeInvoice(blob, invoiceNumber) : await saveInvoicePdfBlob(blob, invoiceNumber);
      if (!preview && android) setMessage(result?.cancelled ? 'Save cancelled. Your invoice is still available.' : 'PDF saved.');
    } catch (e) { setError(e.message || 'Unable to open or save PDF. Please retry.'); }
    finally { setBusy(false); }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title as="h6" className="mb-0">Invoice preview — {invoiceNumber}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0" style={{ minHeight: '60vh' }}>
        {loading && <div className="p-5 text-center"><Spinner animation="border" size="sm" /></div>}
        {error && <Alert variant="danger" className="m-3">{error}</Alert>}
        {message && <Alert variant="info" className="m-3">{message}</Alert>}
        {nativePreview && blob && !loading && <div className="p-3"><p>Preview this invoice in the app or save the PDF to a location you choose.</p><Button disabled={busy} onClick={() => operate(true)}>Open PDF preview</Button></div>}
        {!nativePreview && blob && !loading && <InvoicePdfCanvas blob={blob} />}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>Close</Button>
        <Button variant="success" disabled={!blob || busy} onClick={() => operate(false)}>Download PDF</Button>
      </Modal.Footer>
    </Modal>
  );
}

function FormatTab({ template, onSaved }) {
  const [seller, setSeller] = useState(emptySeller);
  const [emailsText, setEmailsText] = useState(''); // comma-separated - see submit() for the split
  const [bank, setBank] = useState(emptyBank);
  const [defaults, setDefaults] = useState({
    defaultPlaceOfSupply: '', defaultHsnSac: '997222',
    defaultCgstRatePercent: 9, defaultSgstRatePercent: 9, defaultIgstRatePercent: 18,
    defaultInterState: false, invoiceNumberPrefix: 'INV', invoiceNumberFormat: '{PREFIX}-{YY}{SEQ}', sequenceDigits: 3
  });
  const [terms, setTerms] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!template) return;
    const { email: _email, emails: _emails, ...sellerFields } = template.seller || {};
    setSeller({ ...emptySeller, ...sellerFields });
    setEmailsText((_emails || []).join(', '));
    setBank({ ...emptyBank, ...template.bank });
    setDefaults({
      defaultPlaceOfSupply: template.defaultPlaceOfSupply || '',
      defaultHsnSac: template.defaultHsnSac || '997222',
      defaultCgstRatePercent: template.defaultCgstRatePercent ?? 9,
      defaultSgstRatePercent: template.defaultSgstRatePercent ?? 9,
      defaultIgstRatePercent: template.defaultIgstRatePercent ?? 18,
      defaultInterState: !!template.defaultInterState,
      invoiceNumberPrefix: template.invoiceNumberPrefix || 'INV',
      invoiceNumberFormat: template.invoiceNumberFormat || '{PREFIX}-{YY}{SEQ}',
      sequenceDigits: template.sequenceDigits || 3
    });
    setTerms((template.termsAndConditions || []).join('\n'));
  }, [template]);

  // Uploads immediately on file selection (like uploadImages() elsewhere in this app) and just
  // stashes the returned URL on seller.logoUrl - it only actually becomes part of the saved
  // format once "Save format" is clicked, same as every other field on this form. The logo's own
  // aspect ratio is preserved automatically when it's printed (common/invoicePdf.js sizes the box
  // to the image, not the other way round), so nothing needs cropping here.
  const onLogoSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // let the same file be re-picked later (e.g. after removing it)
    if (!file) return;
    setUploadingLogo(true); setError('');
    try {
      const url = await uploadInvoiceLogo(file);
      setSeller((s) => ({ ...s, logoUrl: url }));
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to upload the logo.');
    } finally {
      setUploadingLogo(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError(''); setSaved(false);
    try {
      const payload = {
        seller: { ...seller, email: undefined, emails: emailsText.split(',').map((s) => s.trim()).filter(Boolean) },
        bank,
        ...defaults,
        defaultCgstRatePercent: Number(defaults.defaultCgstRatePercent) || 0,
        defaultSgstRatePercent: Number(defaults.defaultSgstRatePercent) || 0,
        defaultIgstRatePercent: Number(defaults.defaultIgstRatePercent) || 0,
        sequenceDigits: Number(defaults.sequenceDigits) || 3,
        termsAndConditions: terms.split('\n').map((t) => t.trim()).filter(Boolean)
      };
      const data = await saveInvoiceTemplate(payload);
      setSaved(true);
      onSaved(data);
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to save your invoice format.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form onSubmit={submit}>
      <p className="text-muted small">
        Save your business, bank and terms details once — every new invoice starts pre-filled with them.
      </p>
      {error && <Alert variant="danger">{error}</Alert>}
      {saved && <Alert variant="success" dismissible onClose={() => setSaved(false)}>Invoice format saved.</Alert>}

      <div className="border rounded p-3 mb-4">
        <h6>Business (seller) details</h6>
        <Row className="g-3 mb-2">
          <Col md={12} className="d-flex align-items-center gap-3">
            {seller.logoUrl ? (
              <img
                src={imageUrl(seller.logoUrl)} alt="Business logo"
                style={{ maxWidth: 120, maxHeight: 70, objectFit: 'contain', border: '1px solid #dee2e6', borderRadius: 4, background: '#fff', padding: 4 }}
              />
            ) : (
              <div
                className="d-flex align-items-center justify-content-center text-muted small"
                style={{ width: 90, height: 60, border: '1px dashed #adb5bd', borderRadius: 4 }}
              >
                No logo
              </div>
            )}
            <div>
              <Form.Group controlId="invoiceLogoUpload" className="mb-1">
                <Form.Label className="small mb-1">Business logo (printed top-left on the PDF, any aspect ratio)</Form.Label>
                <Form.Control type="file" accept="image/png,image/jpeg,image/webp,image/gif" size="sm" disabled={uploadingLogo} onChange={onLogoSelected} />
              </Form.Group>
              {uploadingLogo && <span className="small text-muted">Uploading…</span>}
              {seller.logoUrl && !uploadingLogo && (
                <Button size="sm" variant="link" className="p-0 text-danger" onClick={() => setSeller((s) => ({ ...s, logoUrl: '' }))}>
                  Remove logo
                </Button>
              )}
            </div>
          </Col>
          <Col md={6}>
            <Form.Group><Form.Label>Business name</Form.Label>
              <Form.Control value={seller.businessName} onChange={(e) => setSeller({ ...seller, businessName: e.target.value })} />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group><Form.Label>GSTIN</Form.Label>
              <Form.Control value={seller.gstin} onChange={(e) => setSeller({ ...seller, gstin: e.target.value })} />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group><Form.Label>PAN</Form.Label>
              <Form.Control value={seller.pan} onChange={(e) => setSeller({ ...seller, pan: e.target.value })} />
            </Form.Group>
          </Col>
          <Col md={12}>
            <Form.Group><Form.Label>Address</Form.Label>
              <Form.Control value={seller.addressLine1} onChange={(e) => setSeller({ ...seller, addressLine1: e.target.value })} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group><Form.Label>City</Form.Label>
              <Form.Control value={seller.city} onChange={(e) => setSeller({ ...seller, city: e.target.value })} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group><Form.Label>State</Form.Label>
              <Form.Control value={seller.state} onChange={(e) => setSeller({ ...seller, state: e.target.value })} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group><Form.Label>Pincode</Form.Label>
              <Form.Control value={seller.pincode} onChange={(e) => setSeller({ ...seller, pincode: e.target.value })} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group><Form.Label>Mobile</Form.Label>
              <Form.Control value={seller.mobile} onChange={(e) => setSeller({ ...seller, mobile: e.target.value })} />
            </Form.Group>
          </Col>
          <Col md={8}>
            <Form.Group>
              <Form.Label>Email(s)</Form.Label>
              <Form.Control
                placeholder="billing@example.com, accounts@example.com"
                value={emailsText}
                onChange={(e) => setEmailsText(e.target.value)}
              />
              <Form.Text muted>Separate multiple addresses with a comma.</Form.Text>
            </Form.Group>
          </Col>
        </Row>
      </div>

      <div className="border rounded p-3 mb-4">
        <h6>Bank details</h6>
        <p className="text-muted small mb-3">Printed on every invoice so the customer knows where to pay.</p>
        <Row className="g-3">
          <Col md={6}>
            <Form.Group><Form.Label>Bank name</Form.Label>
              <Form.Control value={bank.bankName} onChange={(e) => setBank({ ...bank, bankName: e.target.value })} />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group><Form.Label>Account holder</Form.Label>
              <Form.Control value={bank.accountHolder} onChange={(e) => setBank({ ...bank, accountHolder: e.target.value })} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group><Form.Label>Account number</Form.Label>
              <Form.Control value={bank.accountNumber} onChange={(e) => setBank({ ...bank, accountNumber: e.target.value })} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group><Form.Label>IFSC</Form.Label>
              <Form.Control value={bank.ifsc} onChange={(e) => setBank({ ...bank, ifsc: e.target.value })} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group><Form.Label>Branch</Form.Label>
              <Form.Control value={bank.branch} onChange={(e) => setBank({ ...bank, branch: e.target.value })} />
            </Form.Group>
          </Col>
        </Row>
      </div>

      <div className="border rounded p-3 mb-4">
        <h6>Defaults for new invoices</h6>
        <Row className="g-3 mb-2">
          <Col md={4}>
            <Form.Group><Form.Label>Place of supply</Form.Label>
              <Form.Control
                placeholder="e.g. 27-MAHARASHTRA"
                value={defaults.defaultPlaceOfSupply}
                onChange={(e) => setDefaults({ ...defaults, defaultPlaceOfSupply: e.target.value })}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group><Form.Label>HSN/SAC code</Form.Label>
              <Form.Control value={defaults.defaultHsnSac} onChange={(e) => setDefaults({ ...defaults, defaultHsnSac: e.target.value })} />
            </Form.Group>
          </Col>
          <Col md={4} className="d-flex align-items-end">
            <Form.Check
              type="checkbox"
              label="Customers are usually out of state (use IGST instead of CGST+SGST)"
              checked={defaults.defaultInterState}
              onChange={(e) => setDefaults({ ...defaults, defaultInterState: e.target.checked })}
            />
          </Col>
          <Col md={4}>
            <Form.Group><Form.Label>CGST %</Form.Label>
              <Form.Control type="number" min={0} max={100} value={defaults.defaultCgstRatePercent} onChange={(e) => setDefaults({ ...defaults, defaultCgstRatePercent: e.target.value })} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group><Form.Label>SGST %</Form.Label>
              <Form.Control type="number" min={0} max={100} value={defaults.defaultSgstRatePercent} onChange={(e) => setDefaults({ ...defaults, defaultSgstRatePercent: e.target.value })} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group><Form.Label>IGST %</Form.Label>
              <Form.Control type="number" min={0} max={100} value={defaults.defaultIgstRatePercent} onChange={(e) => setDefaults({ ...defaults, defaultIgstRatePercent: e.target.value })} />
            </Form.Group>
          </Col>
        </Row>

        <hr className="my-3" />
        <Row className="g-3">
          <Col md={3}>
            <Form.Group><Form.Label>Invoice number prefix</Form.Label>
              <Form.Control value={defaults.invoiceNumberPrefix} onChange={(e) => setDefaults({ ...defaults, invoiceNumberPrefix: e.target.value })} />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group><Form.Label>Invoice number format</Form.Label>
              <Form.Control value={defaults.invoiceNumberFormat} onChange={(e) => setDefaults({ ...defaults, invoiceNumberFormat: e.target.value })} />
              <Form.Text muted>
                Tokens: <code>{'{PREFIX}'}</code> <code>{'{YY}'}</code> <code>{'{YYYY}'}</code> <code>{'{FY}'}</code> <code>{'{SEQ}'}</code>.
                E.g. <code>{'{PREFIX}:{FY}{SEQ}'}</code> with prefix <code>PU</code> suggests <code>PU:2627001</code>.
              </Form.Text>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group><Form.Label>Sequence digits</Form.Label>
              <Form.Control type="number" min={1} max={8} value={defaults.sequenceDigits} onChange={(e) => setDefaults({ ...defaults, sequenceDigits: e.target.value })} />
            </Form.Group>
          </Col>
        </Row>
        <p className="text-muted small mt-2 mb-0">
          This is only a suggested starting number for each new invoice — freely edit it by hand any time on the New Invoice screen.
        </p>
      </div>

      <Form.Group className="mb-3">
        <Form.Label>Terms and conditions (one per line)</Form.Label>
        <Form.Control as="textarea" rows={3} value={terms} onChange={(e) => setTerms(e.target.value)} />
      </Form.Group>

      <Button type="submit" disabled={saving || uploadingLogo}>{saving ? 'Saving…' : 'Save format'}</Button>
    </Form>
  );
}

// Whether `template` has a remembered last-invoice draft worth pre-filling from - an owner who
// has never generated an invoice (or just cleared the form) has none.
function hasDraft(template) {
  const d = template?.lastInvoiceDraft;
  return !!(d && (d.customer?.name || d.lineItems?.length));
}

function NewInvoiceTab({ template, onCreated, onPreview }) {
  const draft = template?.lastInvoiceDraft;
  const [invoiceNumber, setInvoiceNumber] = useState(template?.suggestedInvoiceNumber || '');
  const [invoiceDate, setInvoiceDate] = useState(todayISO());
  const [dueDate, setDueDate] = useState('');
  const [placeOfSupply, setPlaceOfSupply] = useState(draft?.placeOfSupply || template?.defaultPlaceOfSupply || '');
  const [interState, setInterState] = useState(draft ? !!draft.interState : !!template?.defaultInterState);
  const [cgstRatePercent, setCgstRatePercent] = useState(draft?.cgstRatePercent ?? template?.defaultCgstRatePercent ?? 9);
  const [sgstRatePercent, setSgstRatePercent] = useState(draft?.sgstRatePercent ?? template?.defaultSgstRatePercent ?? 9);
  const [igstRatePercent, setIgstRatePercent] = useState(draft?.igstRatePercent ?? template?.defaultIgstRatePercent ?? 18);
  // Only ever settable when the website admin has explicitly granted it (RentalUser.
  // allowGstExemptInvoices) - hidden entirely for every other owner, never just disabled.
  const [gstExempt, setGstExempt] = useState(false);
  // Remembers the customer + site address + line items from the owner's last generated invoice
  // (see RentalInvoiceTemplate.lastInvoiceDraft) so a repeat invoice doesn't mean retyping them -
  // the invoice number/date above are deliberately NOT remembered, they always start fresh.
  const [customer, setCustomer] = useState(draft?.customer?.name ? { ...emptyCustomer, ...draft.customer } : emptyCustomer);
  const [siteAddress, setSiteAddress] = useState(draft?.siteAddress ? { ...emptyAddress, ...draft.siteAddress } : emptyAddress);
  const [lineItems, setLineItems] = useState(
    draft?.lineItems?.length ? draft.lineItems.map((li) => ({ ...li })) : [newLineItem(template)]
  );
  const [prefilled, setPrefilled] = useState(hasDraft(template));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);

  // "Clear form" - blanks the remembered customer/line items/site address back to a fresh
  // invoice, without touching the saved draft itself (that only changes when an invoice is
  // actually generated).
  const clearForm = () => {
    setCustomer(emptyCustomer);
    setSiteAddress(emptyAddress);
    setLineItems([newLineItem(template)]);
    setPlaceOfSupply(template?.defaultPlaceOfSupply || '');
    setInterState(!!template?.defaultInterState);
    setCgstRatePercent(template?.defaultCgstRatePercent ?? 9);
    setSgstRatePercent(template?.defaultSgstRatePercent ?? 9);
    setIgstRatePercent(template?.defaultIgstRatePercent ?? 18);
    setGstExempt(false);
    setPrefilled(false);
    setError('');
  };

  const setLineItem = (idx, patch) => {
    setLineItems((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const addLineItem = () => setLineItems((rows) => [...rows, newLineItem(template)]);
  const removeLineItem = (idx) => setLineItems((rows) => rows.filter((_, i) => i !== idx));

  const effectiveRate = gstExempt ? 0 : (interState ? Number(igstRatePercent) || 0 : (Number(cgstRatePercent) || 0) + (Number(sgstRatePercent) || 0));
  const preview = lineItems.reduce((sum, li) => sum + (Number(li.rate) || 0) * (Number(li.qty) || 0), 0) * (1 + effectiveRate / 100);

  const submit = async (e) => {
    e.preventDefault();
    if (!customer.name.trim()) { setError("Enter the customer's name."); return; }
    if (lineItems.length === 0 || lineItems.some((li) => !li.description.trim() || !(Number(li.rate) > 0))) {
      setError('Every line item needs a description and a rate greater than 0.');
      return;
    }
    setSubmitting(true); setError(''); setCreated(null);
    try {
      const payload = {
        invoiceNumber: invoiceNumber.trim(),
        invoiceDate,
        dueDate: dueDate || null,
        placeOfSupply,
        interState,
        gstExempt,
        cgstRatePercent: Number(cgstRatePercent) || 0,
        sgstRatePercent: Number(sgstRatePercent) || 0,
        igstRatePercent: Number(igstRatePercent) || 0,
        customer,
        siteAddress,
        lineItems: lineItems.map((li) => ({
          description: li.description.trim(),
          hsnSac: li.hsnSac,
          rate: Number(li.rate) || 0,
          qty: Number(li.qty) || 1
        }))
      };
      const invoice = await createInvoice(payload);
      setCreated(invoice);
      onCreated(invoice);
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to generate this invoice.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form onSubmit={submit}>
      {error && <Alert variant="danger">{error}</Alert>}
      {created && (
        <Alert variant="success" className="d-flex justify-content-between align-items-center">
          <span>Invoice {created.invoiceNumber} generated — {rupees(created.totals?.totalAmount)}.</span>
          <Button size="sm" variant="success" onClick={() => onPreview(created)}>Preview &amp; Download</Button>
        </Alert>
      )}
      {prefilled && (
        <Alert variant="light" className="d-flex justify-content-between align-items-center py-2">
          <span className="small text-muted">Customer, site address and line items are pre-filled from your last invoice.</span>
          <Button size="sm" variant="outline-secondary" onClick={clearForm}>Clear form</Button>
        </Alert>
      )}

      <Row className="g-2 mb-3">
        <Col md={3}>
          <Form.Group><Form.Label>Invoice #</Form.Label>
            <Form.Control required value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group><Form.Label>Invoice date</Form.Label>
            <Form.Control type="date" required value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group><Form.Label>Due date (optional)</Form.Label>
            <Form.Control type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group><Form.Label>Place of supply</Form.Label>
            <Form.Control value={placeOfSupply} onChange={(e) => setPlaceOfSupply(e.target.value)} />
          </Form.Group>
        </Col>
      </Row>

      <div className="border rounded p-3 mb-3">
        {template?.allowGstExemptInvoices && (
          <Form.Check
            className="mb-2"
            type="checkbox"
            label="Bill without GST (no CGST/SGST/IGST at all)"
            checked={gstExempt}
            onChange={(e) => setGstExempt(e.target.checked)}
          />
        )}
        {!gstExempt && (
          <Row className="g-2">
            <Col md={4}>
              <Form.Check
                type="checkbox" label="Customer is out of state (use IGST instead of CGST+SGST)"
                checked={interState} onChange={(e) => setInterState(e.target.checked)}
              />
            </Col>
            {!interState ? (
              <>
                <Col md={4}>
                  <Form.Group><Form.Label className="small mb-1">CGST %</Form.Label>
                    <Form.Control size="sm" type="number" min={0} max={100} value={cgstRatePercent} onChange={(e) => setCgstRatePercent(e.target.value)} />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group><Form.Label className="small mb-1">SGST %</Form.Label>
                    <Form.Control size="sm" type="number" min={0} max={100} value={sgstRatePercent} onChange={(e) => setSgstRatePercent(e.target.value)} />
                  </Form.Group>
                </Col>
              </>
            ) : (
              <Col md={4}>
                <Form.Group><Form.Label className="small mb-1">IGST %</Form.Label>
                  <Form.Control size="sm" type="number" min={0} max={100} value={igstRatePercent} onChange={(e) => setIgstRatePercent(e.target.value)} />
                </Form.Group>
              </Col>
            )}
          </Row>
        )}
      </div>

      <h6>Customer</h6>
      <Row className="g-2 mb-3">
        <Col md={4}>
          <Form.Group><Form.Label>Name</Form.Label>
            <Form.Control required value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group><Form.Label>GSTIN (optional)</Form.Label>
            <Form.Control value={customer.gstin} onChange={(e) => setCustomer({ ...customer, gstin: e.target.value })} />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group><Form.Label>Pincode</Form.Label>
            <Form.Control value={customer.pincode} onChange={(e) => setCustomer({ ...customer, pincode: e.target.value })} />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group><Form.Label>Billing address</Form.Label>
            <Form.Control as="textarea" rows={2} value={customer.addressLine1} onChange={(e) => setCustomer({ ...customer, addressLine1: e.target.value })} />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group><Form.Label>City</Form.Label>
            <Form.Control value={customer.city} onChange={(e) => setCustomer({ ...customer, city: e.target.value })} />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group><Form.Label>State</Form.Label>
            <Form.Control value={customer.state} onChange={(e) => setCustomer({ ...customer, state: e.target.value })} />
          </Form.Group>
        </Col>
      </Row>

      {/* The property/deal address - kept separate from the customer's billing address above, and
          printed as its own column on the PDF (only when filled in). */}
      <h6>Site address <span className="text-muted small fw-normal">(optional, if different from billing address)</span></h6>
      <Row className="g-2 mb-3">
        <Col md={6}>
          <Form.Group><Form.Label>Address</Form.Label>
            <Form.Control as="textarea" rows={2} value={siteAddress.addressLine1} onChange={(e) => setSiteAddress({ ...siteAddress, addressLine1: e.target.value })} />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group><Form.Label>City</Form.Label>
            <Form.Control value={siteAddress.city} onChange={(e) => setSiteAddress({ ...siteAddress, city: e.target.value })} />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group><Form.Label>State</Form.Label>
            <Form.Control value={siteAddress.state} onChange={(e) => setSiteAddress({ ...siteAddress, state: e.target.value })} />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group><Form.Label>Pincode</Form.Label>
            <Form.Control value={siteAddress.pincode} onChange={(e) => setSiteAddress({ ...siteAddress, pincode: e.target.value })} />
          </Form.Group>
        </Col>
      </Row>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="mb-0">Line items</h6>
        <Button size="sm" variant="outline-secondary" onClick={addLineItem}>+ Add line</Button>
      </div>
      <Table responsive size="sm" className="align-middle">
        <thead>
          <tr>
            <th style={{ minWidth: 240 }}>Description</th>
            <th style={{ minWidth: 100 }}>HSN/SAC</th>
            <th style={{ minWidth: 110 }}>Rate</th>
            <th style={{ minWidth: 80 }}>Qty</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {lineItems.map((li, idx) => (
            <tr key={idx}>
              {/* A textarea, not a single-line input - a long description gets more room to grow
                  (and a manual resize handle) rather than scrolling sideways in a fixed box. */}
              <td>
                <Form.Control
                  as="textarea" rows={2} size="sm" style={{ resize: 'vertical', minHeight: 38 }}
                  value={li.description} onChange={(e) => setLineItem(idx, { description: e.target.value })}
                />
              </td>
              <td><Form.Control size="sm" value={li.hsnSac} onChange={(e) => setLineItem(idx, { hsnSac: e.target.value })} /></td>
              <td><Form.Control size="sm" type="number" min={0} value={li.rate} onChange={(e) => setLineItem(idx, { rate: e.target.value })} /></td>
              <td><Form.Control size="sm" type="number" min={0} step="0.01" value={li.qty} onChange={(e) => setLineItem(idx, { qty: e.target.value })} /></td>
              <td>
                {lineItems.length > 1 && (
                  <Button size="sm" variant="outline-danger" onClick={() => removeLineItem(idx)}>&times;</Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div className="d-flex justify-content-between align-items-center mt-3">
        <span className="text-muted small">
          Approx. total {gstExempt ? '(no GST)' : '(tax included)'}: {rupees(preview)}
        </span>
        <Button type="submit" disabled={submitting}>{submitting ? 'Generating…' : 'Generate invoice'}</Button>
      </div>
    </Form>
  );
}

// manual_vs_automatic_invoicing.md - the owner records that their client paid.
function RecordPaymentModal({ invoice, onClose, onDone }) {
  const [form, setForm] = useState({ method: 'BANK_TRANSFER', reference: '', amount: '', receivedAt: todayISO(), note: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setBusy(true); setError('');
    try {
      await recordInvoicePayment(invoice._id, form);
      onDone();
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to record this payment.');
      setBusy(false);
    }
  };

  return (
    <Modal show onHide={onClose}>
      <Modal.Header closeButton><Modal.Title as="h6" className="mb-0">Record payment — {invoice.invoiceNumber}</Modal.Title></Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <p className="text-muted small">
          Record that your client paid you (bank transfer / UPI / cash). Our finance team then verifies it —
          only after that does the amount count toward your revenue.
        </p>
        <Form.Group className="mb-2"><Form.Label className="small">How did they pay?</Form.Label>
          <Form.Select size="sm" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })}>
            {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m.replace(/_/g, ' ').toLowerCase()}</option>)}
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-2"><Form.Label className="small">Reference</Form.Label>
          <Form.Control size="sm" placeholder="UTR / UPI ref / cheque no. / receipt no."
            value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
        </Form.Group>
        <Row className="g-2">
          <Col xs={6}><Form.Group><Form.Label className="small">Amount (optional)</Form.Label>
            <Form.Control size="sm" type="number" placeholder={rupees(invoice.totals?.totalAmount)}
              value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></Form.Group></Col>
          <Col xs={6}><Form.Group><Form.Label className="small">Date received</Form.Label>
            <Form.Control size="sm" type="date" value={form.receivedAt} onChange={(e) => setForm({ ...form, receivedAt: e.target.value })} /></Form.Group></Col>
        </Row>
        <Form.Group className="mt-2"><Form.Label className="small">Note (optional)</Form.Label>
          <Form.Control size="sm" as="textarea" rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={busy || !form.reference.trim()} onClick={submit}>
          {busy ? 'Saving…' : 'Record payment'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// Edit modal for MANUAL invoices — every field on the invoice is editable including seller/bank/
// logo/terms. Backdating is intentionally unrestricted (the owner may need to correct a date).
function EditInvoiceModal({ invoice, template, onClose, onDone }) {
  const [invoiceNumber, setInvoiceNumber] = useState(invoice.invoiceNumber);
  const [invoiceDate, setInvoiceDate] = useState(invoice.invoiceDate?.slice(0, 10) || todayISO());
  const [dueDate, setDueDate] = useState(invoice.dueDate?.slice(0, 10) || '');
  const [placeOfSupply, setPlaceOfSupply] = useState(invoice.placeOfSupply || '');
  const [interState, setInterState] = useState(!!invoice.interState);
  const [gstExempt, setGstExempt] = useState(!!invoice.gstExempt);
  const [cgstRatePercent, setCgstRatePercent] = useState(invoice.cgstRatePercent ?? 9);
  const [sgstRatePercent, setSgstRatePercent] = useState(invoice.sgstRatePercent ?? 9);
  const [igstRatePercent, setIgstRatePercent] = useState(invoice.igstRatePercent ?? 18);
  const [customer, setCustomer] = useState({ ...emptyCustomer, ...(invoice.customer || {}) });
  const [siteAddress, setSiteAddress] = useState({ ...emptyAddress, ...(invoice.siteAddress || {}) });
  const [lineItems, setLineItems] = useState(
    (invoice.lineItems || []).map((li) => ({ description: li.description || '', hsnSac: li.hsnSac || '', rate: li.rate ?? '', qty: li.qty ?? 1 }))
  );
  // Seller/bank/terms pre-filled from the invoice's own stored snapshot (not the current template)
  const { email: _dropEmail, emails: _initEmails, ...initSellerFields } = invoice.seller || {};
  const [seller, setSeller] = useState({ ...emptySeller, ...initSellerFields });
  const [emailsText, setEmailsText] = useState((_initEmails || []).join(', '));
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [bank, setBank] = useState({ ...emptyBank, ...(invoice.bank || {}) });
  const [terms, setTerms] = useState((invoice.termsAndConditions || []).join('\n'));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const setLineItem = (idx, patch) => setLineItems((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const addLineItem = () => setLineItems((rows) => [...rows, newLineItem(template)]);
  const removeLineItem = (idx) => setLineItems((rows) => rows.filter((_, i) => i !== idx));

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const url = await uploadInvoiceLogo(file);
      setSeller((s) => ({ ...s, logoUrl: url }));
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to upload the logo.');
    } finally {
      setUploadingLogo(false);
    }
  };

  const effectiveRate = gstExempt ? 0 : (interState ? Number(igstRatePercent) || 0 : (Number(cgstRatePercent) || 0) + (Number(sgstRatePercent) || 0));
  const preview = lineItems.reduce((sum, li) => sum + (Number(li.rate) || 0) * (Number(li.qty) || 0), 0) * (1 + effectiveRate / 100);

  const submit = async (e) => {
    e.preventDefault();
    if (!customer.name.trim()) { setError("Enter the customer's name."); return; }
    if (lineItems.length === 0 || lineItems.some((li) => !li.description.trim() || !(Number(li.rate) > 0))) {
      setError('Every line item needs a description and a rate greater than 0.');
      return;
    }
    setSubmitting(true); setError('');
    try {
      const payload = {
        invoiceNumber: invoiceNumber.trim(),
        invoiceDate,
        dueDate: dueDate || null,
        placeOfSupply,
        interState,
        gstExempt,
        cgstRatePercent: Number(cgstRatePercent) || 0,
        sgstRatePercent: Number(sgstRatePercent) || 0,
        igstRatePercent: Number(igstRatePercent) || 0,
        seller: { ...seller, email: undefined, emails: emailsText.split(',').map((s) => s.trim()).filter(Boolean) },
        bank,
        customer,
        siteAddress,
        termsAndConditions: terms.split('\n').map((t) => t.trim()).filter(Boolean),
        lineItems: lineItems.map((li) => ({
          description: li.description.trim(),
          hsnSac: li.hsnSac,
          rate: Number(li.rate) || 0,
          qty: Number(li.qty) || 1
        }))
      };
      await updateInvoice(invoice._id, payload);
      onDone();
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to save changes.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show onHide={onClose} size="xl" scrollable>
      <Modal.Header closeButton>
        <Modal.Title as="h6">Edit invoice — {invoice.invoiceNumber}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form id="edit-invoice-form" onSubmit={submit}>
          {error && <Alert variant="danger">{error}</Alert>}
          <Row className="g-2 mb-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Invoice #</Form.Label>
                <Form.Control required value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} />
                {invoiceNumber !== invoice.invoiceNumber && (
                  <Form.Text className="text-warning">
                    If you've already shared this invoice, changing the number may cause a mismatch with the copy your customer has.
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group><Form.Label>Invoice date</Form.Label>
                <Form.Control type="date" required value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group><Form.Label>Due date (optional)</Form.Label>
                <Form.Control type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group><Form.Label>Place of supply</Form.Label>
                <Form.Control value={placeOfSupply} onChange={(e) => setPlaceOfSupply(e.target.value)} />
              </Form.Group>
            </Col>
          </Row>
          <div className="border rounded p-3 mb-3">
            {template?.allowGstExemptInvoices && (
              <Form.Check className="mb-2" type="checkbox" label="Bill without GST (no CGST/SGST/IGST at all)"
                checked={gstExempt} onChange={(e) => setGstExempt(e.target.checked)} />
            )}
            {!gstExempt && (
              <Row className="g-2">
                <Col md={4}>
                  <Form.Check type="checkbox" label="Customer is out of state (use IGST instead of CGST+SGST)"
                    checked={interState} onChange={(e) => setInterState(e.target.checked)} />
                </Col>
                {!interState ? (
                  <>
                    <Col md={4}>
                      <Form.Group><Form.Label className="small mb-1">CGST %</Form.Label>
                        <Form.Control size="sm" type="number" min={0} max={100} value={cgstRatePercent} onChange={(e) => setCgstRatePercent(e.target.value)} />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group><Form.Label className="small mb-1">SGST %</Form.Label>
                        <Form.Control size="sm" type="number" min={0} max={100} value={sgstRatePercent} onChange={(e) => setSgstRatePercent(e.target.value)} />
                      </Form.Group>
                    </Col>
                  </>
                ) : (
                  <Col md={4}>
                    <Form.Group><Form.Label className="small mb-1">IGST %</Form.Label>
                      <Form.Control size="sm" type="number" min={0} max={100} value={igstRatePercent} onChange={(e) => setIgstRatePercent(e.target.value)} />
                    </Form.Group>
                  </Col>
                )}
              </Row>
            )}
          </div>
          <h6>Customer</h6>
          <Row className="g-2 mb-3">
            <Col md={4}>
              <Form.Group><Form.Label>Name</Form.Label>
                <Form.Control required value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group><Form.Label>GSTIN (optional)</Form.Label>
                <Form.Control value={customer.gstin} onChange={(e) => setCustomer({ ...customer, gstin: e.target.value })} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group><Form.Label>Pincode</Form.Label>
                <Form.Control value={customer.pincode} onChange={(e) => setCustomer({ ...customer, pincode: e.target.value })} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group><Form.Label>Billing address</Form.Label>
                <Form.Control as="textarea" rows={2} value={customer.addressLine1} onChange={(e) => setCustomer({ ...customer, addressLine1: e.target.value })} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group><Form.Label>City</Form.Label>
                <Form.Control value={customer.city} onChange={(e) => setCustomer({ ...customer, city: e.target.value })} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group><Form.Label>State</Form.Label>
                <Form.Control value={customer.state} onChange={(e) => setCustomer({ ...customer, state: e.target.value })} />
              </Form.Group>
            </Col>
          </Row>
          <h6>Site address <span className="text-muted small fw-normal">(optional)</span></h6>
          <Row className="g-2 mb-3">
            <Col md={6}>
              <Form.Group><Form.Label>Address</Form.Label>
                <Form.Control as="textarea" rows={2} value={siteAddress.addressLine1} onChange={(e) => setSiteAddress({ ...siteAddress, addressLine1: e.target.value })} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group><Form.Label>City</Form.Label>
                <Form.Control value={siteAddress.city} onChange={(e) => setSiteAddress({ ...siteAddress, city: e.target.value })} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group><Form.Label>State</Form.Label>
                <Form.Control value={siteAddress.state} onChange={(e) => setSiteAddress({ ...siteAddress, state: e.target.value })} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group><Form.Label>Pincode</Form.Label>
                <Form.Control value={siteAddress.pincode} onChange={(e) => setSiteAddress({ ...siteAddress, pincode: e.target.value })} />
              </Form.Group>
            </Col>
          </Row>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="mb-0">Line items</h6>
            <Button size="sm" variant="outline-secondary" onClick={addLineItem}>+ Add line</Button>
          </div>
          <Table responsive size="sm" className="align-middle">
            <thead>
              <tr>
                <th style={{ minWidth: 240 }}>Description</th>
                <th style={{ minWidth: 100 }}>HSN/SAC</th>
                <th style={{ minWidth: 110 }}>Rate</th>
                <th style={{ minWidth: 80 }}>Qty</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((li, idx) => (
                <tr key={idx}>
                  <td>
                    <Form.Control as="textarea" rows={2} size="sm" style={{ resize: 'vertical', minHeight: 38 }}
                      value={li.description} onChange={(e) => setLineItem(idx, { description: e.target.value })} />
                  </td>
                  <td><Form.Control size="sm" value={li.hsnSac} onChange={(e) => setLineItem(idx, { hsnSac: e.target.value })} /></td>
                  <td><Form.Control size="sm" type="number" min={0} value={li.rate} onChange={(e) => setLineItem(idx, { rate: e.target.value })} /></td>
                  <td><Form.Control size="sm" type="number" min={0} step="0.01" value={li.qty} onChange={(e) => setLineItem(idx, { qty: e.target.value })} /></td>
                  <td>
                    {lineItems.length > 1 && (
                      <Button size="sm" variant="outline-danger" onClick={() => removeLineItem(idx)}>&times;</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <div className="text-end text-muted small mt-2">
            Approx. total {gstExempt ? '(no GST)' : '(tax included)'}: {rupees(preview)}
          </div>

          <hr className="my-4" />
          <h6>Seller / business details</h6>
          <Row className="g-2 mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Logo</Form.Label>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  {seller.logoUrl && <img src={imageUrl(seller.logoUrl)} alt="logo" style={{ height: 48, objectFit: 'contain', border: '1px solid #dee2e6', borderRadius: 4 }} />}
                  <Form.Control type="file" accept="image/*" style={{ maxWidth: 280 }} onChange={uploadLogo} disabled={uploadingLogo} />
                  {uploadingLogo && <span className="small text-muted">Uploading…</span>}
                  {seller.logoUrl && !uploadingLogo && (
                    <Button size="sm" variant="link" className="p-0 text-danger" onClick={() => setSeller((s) => ({ ...s, logoUrl: '' }))}>Remove</Button>
                  )}
                </div>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group><Form.Label>Business name</Form.Label>
                <Form.Control value={seller.businessName} onChange={(e) => setSeller({ ...seller, businessName: e.target.value })} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group><Form.Label>GSTIN</Form.Label>
                <Form.Control value={seller.gstin} onChange={(e) => setSeller({ ...seller, gstin: e.target.value })} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group><Form.Label>PAN</Form.Label>
                <Form.Control value={seller.pan} onChange={(e) => setSeller({ ...seller, pan: e.target.value })} />
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group><Form.Label>Address</Form.Label>
                <Form.Control value={seller.addressLine1} onChange={(e) => setSeller({ ...seller, addressLine1: e.target.value })} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group><Form.Label>City</Form.Label>
                <Form.Control value={seller.city} onChange={(e) => setSeller({ ...seller, city: e.target.value })} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group><Form.Label>State</Form.Label>
                <Form.Control value={seller.state} onChange={(e) => setSeller({ ...seller, state: e.target.value })} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group><Form.Label>Pincode</Form.Label>
                <Form.Control value={seller.pincode} onChange={(e) => setSeller({ ...seller, pincode: e.target.value })} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group><Form.Label>Mobile</Form.Label>
                <Form.Control value={seller.mobile} onChange={(e) => setSeller({ ...seller, mobile: e.target.value })} />
              </Form.Group>
            </Col>
            <Col md={8}>
              <Form.Group>
                <Form.Label>Email(s) <span className="text-muted small fw-normal">(comma-separated)</span></Form.Label>
                <Form.Control value={emailsText} onChange={(e) => setEmailsText(e.target.value)} placeholder="billing@example.com, accounts@example.com" />
              </Form.Group>
            </Col>
          </Row>

          <h6>Bank details</h6>
          <Row className="g-2 mb-3">
            <Col md={4}>
              <Form.Group><Form.Label>Bank name</Form.Label>
                <Form.Control value={bank.bankName} onChange={(e) => setBank({ ...bank, bankName: e.target.value })} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group><Form.Label>Account holder</Form.Label>
                <Form.Control value={bank.accountHolder} onChange={(e) => setBank({ ...bank, accountHolder: e.target.value })} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group><Form.Label>Account number</Form.Label>
                <Form.Control value={bank.accountNumber} onChange={(e) => setBank({ ...bank, accountNumber: e.target.value })} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group><Form.Label>IFSC</Form.Label>
                <Form.Control value={bank.ifsc} onChange={(e) => setBank({ ...bank, ifsc: e.target.value })} />
              </Form.Group>
            </Col>
            <Col md={8}>
              <Form.Group><Form.Label>Branch</Form.Label>
                <Form.Control value={bank.branch} onChange={(e) => setBank({ ...bank, branch: e.target.value })} />
              </Form.Group>
            </Col>
          </Row>

          <h6>Terms &amp; conditions</h6>
          <Form.Group className="mb-3">
            <Form.Control as="textarea" rows={4} value={terms} onChange={(e) => setTerms(e.target.value)}
              placeholder="One term per line" />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button type="submit" form="edit-invoice-form" disabled={submitting}>
          {submitting ? 'Saving…' : 'Save changes'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function HistoryTab({ invoices, template, onPreview, onChanged }) {
  const [downloadMessage, setDownloadMessage] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);
  const download = async (invoice) => {
    if (downloadingId) return;
    setDownloadingId(invoice._id); setDownloadMessage('');
    try {
      const result = await downloadInvoicePdf(invoice._id, invoice.invoiceNumber);
      if (isAndroidInvoice()) setDownloadMessage(result?.cancelled ? 'Save cancelled. Your invoice is still available.' : 'PDF saved.');
    } catch (e) { setDownloadMessage(e.message || 'Unable to download PDF. Please retry.'); }
    finally { setDownloadingId(null); }
  };
  const [busyId, setBusyId] = useState(null);
  const [payTarget, setPayTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');

  const filtered = invoices.filter((inv) => {
    if (typeFilter === 'manual') return inv.generationType === 'MANUAL';
    if (typeFilter === 'automatic') return inv.generationType === 'AUTOMATIC';
    return true;
  });
  const paged = usePagedArray(filtered);

  const toggleStatus = async (inv) => {
    setBusyId(inv._id);
    try {
      await setInvoiceStatus(inv._id, inv.status === 'disabled' ? 'active' : 'disabled');
      onChanged();
    } finally {
      setBusyId(null);
    }
  };

  if (invoices.length === 0) return <Alert variant="light">No invoices generated yet.</Alert>;

  const manualCount = invoices.filter((i) => i.generationType === 'MANUAL').length;
  const autoCount = invoices.filter((i) => i.generationType === 'AUTOMATIC').length;

  return (
    <>
      {downloadMessage && <Alert variant="info" role="status">{downloadMessage}</Alert>}
      <div className="d-flex gap-2 mb-3">
        {[
          ['all', `All (${invoices.length})`],
          ['manual', `Manual (${manualCount})`],
          ['automatic', `Automatic (${autoCount})`]
        ].map(([key, label]) => (
          <Button key={key} size="sm" variant={typeFilter === key ? 'primary' : 'outline-secondary'} onClick={() => setTypeFilter(key)}>
            {label}
          </Button>
        ))}
      </div>
      {filtered.length === 0 && <Alert variant="light">No invoices match this filter.</Alert>}
      {filtered.length > 0 && (
        <>
          <Table responsive hover>
            <thead>
              <tr><th>Invoice #</th><th>Date</th><th>Customer</th><th>Amount</th><th>Type</th><th>Payment</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {paged.rows.map((inv) => (
                <tr key={inv._id} className={inv.status === 'disabled' ? 'table-secondary' : undefined}>
                  <td className="font-monospace">{inv.invoiceNumber}</td>
                  <td>{new Date(inv.invoiceDate).toLocaleDateString('en-IN')}</td>
                  <td>{inv.customer?.name}</td>
                  <td>{rupees(inv.totals?.totalAmount)}</td>
                  <td>
                    <Badge bg={inv.generationType === 'MANUAL' ? 'info' : 'secondary'} text={inv.generationType === 'MANUAL' ? 'dark' : undefined}>
                      {inv.generationType === 'MANUAL' ? 'Manual' : 'Auto'}
                    </Badge>
                  </td>
                  <td>{paymentBadge(inv)}</td>
                  <td>
                    <Badge bg={inv.status === 'disabled' ? 'secondary' : 'success'}>{inv.status === 'disabled' ? 'Disabled' : 'Active'}</Badge>
                    {inv.gstExempt && <Badge bg="info" className="ms-1">No GST</Badge>}
                  </td>
                  <td>
                    <div className="d-flex gap-2 flex-wrap">
                      <Button size="sm" variant="outline-primary" onClick={() => onPreview(inv)}>Preview</Button>
                      <Button size="sm" variant="outline-secondary" disabled={!!downloadingId} onClick={() => download(inv)}>Download</Button>
                      {inv.generationType === 'MANUAL' && !inv.revenueRecognisedAt && inv.status !== 'disabled' && (
                        <Button size="sm" variant="outline-warning" onClick={() => setEditTarget(inv)}>Edit</Button>
                      )}
                      {inv.status !== 'disabled' && !inv.revenueRecognisedAt && (
                        <Button size="sm" variant="outline-success" onClick={() => setPayTarget(inv)}>
                          {inv.paymentStatus === 'PAID' || inv.paymentStatus === 'PARTIALLY_PAID' ? 'Update payment' : 'Record payment'}
                        </Button>
                      )}
                      <Button
                        size="sm" variant={inv.status === 'disabled' ? 'outline-success' : 'outline-danger'}
                        disabled={busyId === inv._id} onClick={() => toggleStatus(inv)}
                      >
                        {inv.status === 'disabled' ? 'Enable' : 'Disable'}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Pager {...paged} />
        </>
      )}
      {payTarget && (
        <RecordPaymentModal invoice={payTarget} onClose={() => setPayTarget(null)}
          onDone={() => { setPayTarget(null); onChanged(); }} />
      )}
      {editTarget && (
        <EditInvoiceModal invoice={editTarget} template={template} onClose={() => setEditTarget(null)}
          onDone={() => { setEditTarget(null); onChanged(); }} />
      )}
    </>
  );
}

// The "separate revenue line" for the owner's own invoicing.
function InvoiceSummaryStrip({ summary }) {
  if (!summary) return null;
  const cell = (label, value, sub, variant) => (
    <Col xs={12} md={4}>
      <div className={`border rounded p-2 h-100 border-${variant || 'secondary'}-subtle`}>
        <div className="small text-muted">{label}</div>
        <div className="fw-bold">{value}</div>
        {sub && <div className="small text-muted">{sub}</div>}
      </div>
    </Col>
  );
  return (
    <>
      <Row className="g-2 mb-2">
        {cell('Invoiced & verified', rupees(summary.verified.taxableTotal), `${summary.verified.count} invoice${summary.verified.count === 1 ? '' : 's'} · counts as your revenue`, 'success')}
        {cell('Paid · awaiting verification', rupees(summary.awaitingVerification.total), `${summary.awaitingVerification.count} invoice${summary.awaitingVerification.count === 1 ? '' : 's'}`, 'warning')}
        {cell('Unpaid', rupees(summary.unpaid.total), `${summary.unpaid.count} invoice${summary.unpaid.count === 1 ? '' : 's'}`, 'secondary')}
      </Row>
      <p className="text-muted small">
        These are your own business invoices to your clients. They don&apos;t affect any platform figure — an amount only
        counts toward your revenue once our finance team has verified the payment.
      </p>
    </>
  );
}

export default function InvoicesPanel() {
  const [template, setTemplate] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('new');
  const [previewInvoice, setPreviewInvoice] = useState(null);
  // Bumped after every successful create/save so the form components remount and re-derive their
  // defaults (suggested invoice number, saved seller/bank fields) from the freshly-reloaded state,
  // instead of clobbering whatever the owner is mid-typing.
  const [formKey, setFormKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    const [templateResult, invoicesResult, summaryResult] = await Promise.allSettled([
      getInvoiceTemplate(), listInvoices(), getInvoiceSummary()
    ]);
    if (templateResult.status === 'fulfilled') setTemplate(templateResult.value);
    if (invoicesResult.status === 'fulfilled') setInvoices(invoicesResult.value);
    if (summaryResult.status === 'fulfilled') setSummary(summaryResult.value);
    if (templateResult.status === 'rejected' || invoicesResult.status === 'rejected') {
      setError('Could not load your invoices. Showing what did load.');
    }
    setLoading(false);
    setFormKey((k) => k + 1);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Spinner animation="border" role="status" />;

  return (
    <div>
      {error && <Alert variant="danger">{error}</Alert>}
      <ul className="nav nav-pills mb-3">
        {[['new', 'New Invoice'], ['format', 'Invoice Format'], ['history', `History (${invoices.length})`]].map(([key, label]) => (
          <li className="nav-item" key={key}>
            <button
              type="button"
              className={`nav-link ${tab === key ? 'active' : ''}`}
              onClick={() => setTab(key)}
            >
              {label}
            </button>
          </li>
        ))}
      </ul>

      {tab === 'new' && <NewInvoiceTab key={formKey} template={template} onCreated={load} onPreview={setPreviewInvoice} />}
      {tab === 'format' && <FormatTab key={`format-${formKey}`} template={template} onSaved={load} />}
      {tab === 'history' && (
        <>
          <InvoiceSummaryStrip summary={summary} />
          <HistoryTab invoices={invoices} template={template} onPreview={setPreviewInvoice} onChanged={load} />
        </>
      )}

      <PdfPreviewModal
        invoiceId={previewInvoice?._id}
        invoiceNumber={previewInvoice?.invoiceNumber}
        show={!!previewInvoice}
        onHide={() => setPreviewInvoice(null)}
      />
    </div>
  );
}
